import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

async function fileToBase64(file, max = 1400) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.floor(width * s); height = Math.floor(height * s); }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject; img.src = r.result;
    };
    r.onerror = reject; r.readAsDataURL(file);
  });
}

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = async () => { const { data } = await api.get("/gallery"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { title: open.item.title, caption: open.item.caption, image: open.item.image, tags: open.item.tags || [], order: open.item.order || 0 };
      if (open.mode === "new") await api.post("/admin/gallery", payload);
      else await api.put(`/admin/gallery/${open.item.item_id}`, payload);
      toast.success("Saved"); setOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this photo?")) return; await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); load(); };
  const onImage = async (file) => { const b64 = await fileToBase64(file); setOpen({ ...open, item: { ...open.item, image: b64 } }); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">Your installation portfolio — shown on the home page.</p>
        </div>
        <Button onClick={() => setOpen({ mode: "new", item: { title: "", caption: "", image: "", order: items.length } })} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="gallery-add-btn"><Plus className="h-4 w-4 mr-2" />Add photo</Button>
      </div>
      {items.length === 0 ? (
        <Card className="p-16 text-center text-muted-foreground">No photos yet.</Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map(g => (
            <Card key={g.item_id} className="p-3">
              <div className="aspect-square rounded-lg overflow-hidden bg-secondary mb-3"><img src={g.image} alt={g.title} className="w-full h-full object-cover" /></div>
              <div className="font-medium text-sm truncate">{g.title || "Untitled"}</div>
              <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{g.caption}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setOpen({ mode: "edit", item: g })}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(g.item_id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.mode === "new" ? "Add photo" : "Edit photo"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Image</Label>
                  <div className="aspect-video rounded-lg bg-secondary border border-border overflow-hidden flex items-center justify-center mb-2">
                    {open.item.image ? <img src={open.item.image} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-muted-foreground">No image</span>}
                  </div>
                  <label className="cursor-pointer inline-block">
                    <Button variant="outline" asChild><span>Upload</span></Button>
                    <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onImage(e.target.files[0])} />
                  </label>
                  <Input placeholder="Or paste image URL" value={open.item.image?.startsWith("http") ? open.item.image : ""} onChange={(e) => setOpen({ ...open, item: { ...open.item, image: e.target.value } })} className="mt-2" />
                </div>
                <div><Label>Title</Label><Input value={open.item.title} onChange={(e) => setOpen({ ...open, item: { ...open.item, title: e.target.value } })} data-testid="gallery-title" /></div>
                <div><Label>Caption</Label><Textarea rows={2} value={open.item.caption} onChange={(e) => setOpen({ ...open, item: { ...open.item, caption: e.target.value } })} /></div>
                <div><Label>Order</Label><Input type="number" value={open.item.order || 0} onChange={(e) => setOpen({ ...open, item: { ...open.item, order: parseInt(e.target.value || 0) } })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
                <Button onClick={save} disabled={!open.item.image} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="gallery-save-btn">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
